import type { ReactNode } from 'react';
import { cn } from './cn';

export type AlertType = 'info' | 'success' | 'warning' | 'error';

const STYLES: Record<AlertType, string> = {
  info: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  success: 'border-success-500/30 bg-success-500/10 text-success-600 dark:text-success-400',
  warning: 'border-warning-500/40 bg-warning-500/12 text-harvest-700 dark:text-harvest-300',
  error: 'border-danger-500/30 bg-danger-500/10 text-danger-600 dark:text-danger-400',
};

export interface AlertProps {
  type?: AlertType;
  title?: ReactNode;
  /** antd alias for `title`. */
  message?: ReactNode;
  description?: ReactNode;
  /** Optional leading icon (pass an icon element). */
  icon?: ReactNode;
  className?: string;
  style?: import('react').CSSProperties;
  showIcon?: boolean;
  banner?: boolean;
  closable?: boolean;
  action?: ReactNode;
  children?: ReactNode;
}

/** Inline message banner (replaces antd Alert). */
export function Alert({ type = 'info', title, message, description, icon, className, style, action, children }: AlertProps) {
  return (
    <div style={style} className={cn('flex gap-2.5 rounded-control border px-3.5 py-2.5', STYLES[type], className)}>
      {action != null && <div className="order-last ml-auto shrink-0 self-center">{action}</div>}
      {icon != null && <span className="mt-0.5 shrink-0 leading-none [&>svg]:size-[18px]">{icon}</span>}
      <div className="min-w-0 text-sm">
        {(title ?? message) != null && <div className="font-semibold">{title ?? message}</div>}
        {description != null && <div className="mt-0.5 text-[13px] opacity-90">{description}</div>}
        {children}
      </div>
    </div>
  );
}
